import React, { useCallback, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import moment from 'moment';

import { WhiteHeaderMUIDataGrid } from 'components/ThemedMUIDataGrid/WhiteHeaderMUIDataGrid';

import FinBudgetExpenseFixedApi from '../../../../../api/finance/FinBudgetExpenseFixedApi';
import { DD_SLASH_MMM_SLASH_YYYY } from '../../../../../constants/dateFormat';
import { toBill, toCoins } from '../../../../../services/financial';
import { onRequestError } from '../../../../../services/handlers';
import { muiDataGridLocaleText } from '../../../../../services/i18n/mui';
import { Amount } from '../../../finance/shared/amount-view/Amount/Amount';
import RightPanel from '../../../setup/shared/ElementSetupNew/TableSetup/TableSettings/EditTableLabel/RightPanel';
import { ADD_FIXED_EXPENSE, SHOW_FIXED_EXPENSE } from '../ExpenseFixedConstants';

import { AddFixedExpense } from './AddFixedExpense/AddFixedExpense';
import { FullFixedExpenseInformation } from './FullExpenseFixedInformation/FullFixedExpenseInformation';

export const ExpenseFixedList = ({
  expensesFixed,
  isUserHaveAccessToManageExpenses,
  updateAvailableExpenseFixed,
  selectedSSU,
  showRightPanel,
  setShowRightPanel
}) => {
  const [editedExpensesFixed, setEditedExpensesFixed] = useState([]);
  const [editCommentMode, setEditCommentMode] = useState(false);
  const [editedComment, setEditedComment] = useState('');
  const [newFixedExpenseData, setNewFixedExpenseData] = useState(null);

  const onEditComment = useCallback(
    row => {
      const allRowsWithSameId = expensesFixed.filter(expense => expense.expenseFixedId === row.expenseFixedId);
      setShowRightPanel(SHOW_FIXED_EXPENSE);
      setEditedExpensesFixed(allRowsWithSameId);
      setEditCommentMode(false);
      setEditedComment('');
    },
    [expensesFixed, setShowRightPanel]
  );

  const closeRightPanel = useCallback(() => {
    setShowRightPanel(null);
    setEditedComment('');
    setEditedExpensesFixed([]);
    setNewFixedExpenseData(null);
  }, [setShowRightPanel]);

  const saveNewFixedExpense = useCallback(() => {
    FinBudgetExpenseFixedApi.create(selectedSSU.ssuIdentifier, {
      studyId: selectedSSU.studyIdentifier,
      siteId: selectedSSU.siteIdentifier,
      ...newFixedExpenseData
    }).then(() => {
      setShowRightPanel(null);
      updateAvailableExpenseFixed();
    }, onRequestError);
  }, [
    newFixedExpenseData,
    selectedSSU.siteIdentifier,
    selectedSSU.ssuIdentifier,
    selectedSSU.studyIdentifier,
    setShowRightPanel,
    updateAvailableExpenseFixed
  ]);

  const saveUpdatedComment = useCallback(() => {
    FinBudgetExpenseFixedApi.updateCommentForExpenseFixed(selectedSSU?.ssuIdentifier, {
      comment: editedComment.trim(),
      finBudgetExpenseFixedId: editedExpensesFixed[0]?.expenseFixedId
    }).then(() => {
      setShowRightPanel(null);
      setEditCommentMode(false);
      setEditedComment('');
      setEditedExpensesFixed([]);
      updateAvailableExpenseFixed();
    }, onRequestError);
  }, [selectedSSU?.ssuIdentifier, editedComment, editedExpensesFixed, setShowRightPanel, updateAvailableExpenseFixed]);

  const columns = useMemo(() => {
    const columns = [
      {
        field: 'date',
        headerName: 'Event Date',
        minWidth: 140,
        type: 'date',
        valueGetter: value => value && new Date(value),
        renderCell: ({ row: { date } }) => <div>{date ? moment(date).format(DD_SLASH_MMM_SLASH_YYYY) : '—'}</div>
      },
      {
        field: 'elligoRevenueDate',
        headerName: 'Revenue Date',
        minWidth: 160,
        type: 'date',
        valueGetter: value => value && new Date(value),
        renderCell: ({ row: { elligoRevenueDate } }) => (
          <div>{elligoRevenueDate ? moment(elligoRevenueDate).format(DD_SLASH_MMM_SLASH_YYYY) : 'N/A'}</div>
        )
      },
      {
        field: 'event',
        headerName: 'Fixed Expense - Event Name',
        flex: 1,
        minWidth: 280
      },
      {
        field: 'eventNumber',
        headerName: 'Event ID',
        flex: 1,
        minWidth: 120,
        valueGetter(value, row) {
          if (row?.adjustmentSequence) {
            return `${value}.${row.adjustmentSequence}`;
          } else {
            return value;
          }
        }
      },
      {
        field: 'clientAmount',
        headerName: 'Amount',
        flex: 1,
        minWidth: 175,
        type: 'number',
        valueGetter: value => toBill(toCoins(value)),
        renderCell: ({ row }) => <Amount coinsAmount={toCoins(row.clientAmount)} showDollarSign />
      },
      {
        field: 'eventStatus',
        headerName: 'Status',
        flex: 1,
        minWidth: 110
      },
      {
        field: 'addedBy',
        headerName: 'Added By',
        flex: 1,
        minWidth: 130
      },
      {
        field: 'comments',
        headerName: 'Comments',
        flex: 1,
        minWidth: 400,
        valueGetter: (value, row) => row.comments[0]?.comment,
        renderCell: ({ value }) => (
          <Tooltip title={value} placement="top" arrow>
            <div className="overflow-ellipsis">{value}</div>
          </Tooltip>
        )
      }
    ];

    if (!isUserHaveAccessToManageExpenses) {
      return columns.filter(column => !['clientAmount'].includes(column.field));
    }
    return columns;
  }, [isUserHaveAccessToManageExpenses]);

  return (
    <div>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        {isUserHaveAccessToManageExpenses && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            sx={{ textTransform: 'none', lineHeight: 'unset' }}
            onClick={() => setShowRightPanel(ADD_FIXED_EXPENSE)}
            data-testid="add-fixed-expense"
          >
            Fixed Expense
          </Button>
        )}
      </Box>
      <div>
        <WhiteHeaderMUIDataGrid
          sx={{
            height: '500px',
            border: 'none'
          }}
          columns={columns}
          rows={expensesFixed}
          getRowId={row => row?.ledgerEventId}
          rowHeight={38}
          localeText={muiDataGridLocaleText({ noRowsLabel: 'No Record Found' })}
          onRowClick={({ row }) => {
            onEditComment(row);
          }}
          disableRowSelectionOnClick
          disableRowGrouping
          disableAggregation
          disablePivoting
          showToolbar
          slotProps={{
            root: { 'data-testid': 'expenses-fixed-table' },
            toolbar: {
              printOptions: { disableToolbarButton: true },
              excelOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true }
            }
          }}
        />
      </div>
      {showRightPanel === SHOW_FIXED_EXPENSE && (
        <RightPanel
          header="Fixed Expense Details"
          toggleClose={closeRightPanel}
          onSave={saveUpdatedComment}
          hideFooter={!isUserHaveAccessToManageExpenses}
          disableSaveButton={!editCommentMode}
        >
          <FullFixedExpenseInformation
            editCommentMode={editCommentMode}
            editedComment={editedComment}
            setEditedComment={setEditedComment}
            setEditCommentMode={setEditCommentMode}
            editedExpensesFixed={editedExpensesFixed}
            isUserHaveAccessToManageExpenses={isUserHaveAccessToManageExpenses}
          />
        </RightPanel>
      )}
      {showRightPanel === ADD_FIXED_EXPENSE && (
        <RightPanel
          header="Add Expense - Fixed"
          toggleClose={closeRightPanel}
          onSave={saveNewFixedExpense}
          hideFooter={!isUserHaveAccessToManageExpenses}
          disableSaveButton={!newFixedExpenseData?.finTriggerId}
          saveButtonText="Add"
        >
          <AddFixedExpense selectedSSU={selectedSSU} setNewFixedExpenseData={setNewFixedExpenseData} />
        </RightPanel>
      )}
    </div>
  );
};
